import { ArrowDownTrayIcon } from '@heroicons/react/24/solid'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, Resolver, useForm } from 'react-hook-form'

import TrainingDataCsvDownloadModal from '../../../features/ConfigureDataCollectionAdminPanelPage/TrainingDataCsvDownloadModal'
import { Button, Toggle, Tooltip } from '../../designs'
import { useEnhancedNavigate } from '../../hooks'
import { dataCollectionGranularityFormSchema } from '../DataCollectionForms/formSchemas'
import { ContentDisplay } from '../displays'
import { DataCollectionGranularityForm } from './types'

interface Props {
  formData: DataCollectionGranularityForm
  onUpdateGranularityData: (formData: DataCollectionGranularityForm) => Promise<void>
  appName: string
}

const DataCollectionGranularity = ({ formData, onUpdateGranularityData, appName }: Props) => {
  const { enhancedNavigate: navigate } = useEnhancedNavigate()
  const { control, handleSubmit, reset } = useForm<DataCollectionGranularityForm>({
    resolver: yupResolver(
      dataCollectionGranularityFormSchema,
    ) as Resolver<DataCollectionGranularityForm>,
    defaultValues: formData,
  })
  const [isTrainingDataCsvDownloadModalOpen, setIsTrainingDataCsvDownloadModalOpen] =
    useState(false)

  useEffect(() => {
    reset(formData)
  }, [reset, formData])

  const onNavigateToTrainingDataStudio = () => {
    navigate('./trainig-data')
  }

  return (
    <>
      <ContentDisplay className='p-4'>
        <Tooltip
          size='7xl'
          text='The following data collection configs you configure, along with the behavior of target systems, may result in the collection of personal data. It is your responsibility to ensure compliance with applicable privacy regulations and company policies. ProcessMaker provides configuration options to minimize data collection and protect user privacy. Please proceed with caution and only collect necessary business process data. Misuse or improper configuration may lead to unintended data exposure.'
          variant='info'
          placement='r'
        />
        <form
          onSubmit={handleSubmit(onUpdateGranularityData)}
          className='space-y-2'
          id='config-wizard-form'
        >
          <Controller
            name='isUrlCaptured'
            control={control}
            render={({ field }) => (
              <Toggle size='xs' label='Capture full URLs for training data' {...field} />
            )}
          />

          <Controller
            name='isTitleCaptured'
            control={control}
            render={({ field }) => (
              <Toggle size='xs' label='Capture full screen titles for training data' {...field} />
            )}
          />

          <Controller
            name='isTypingCaptured'
            control={control}
            render={({ field }) => <Toggle size='xs' label='Record typed text' {...field} />}
          />

          <Controller
            name='isClipboardContentCaptured'
            control={control}
            render={({ field }) => <Toggle size='xs' label='Record clipboard content' {...field} />}
          />

          <Controller
            name='isKeyboardShortcutsCaptured'
            control={control}
            render={({ field }) => (
              <Toggle size='xs' label='Record used keyboard shortcuts' {...field} />
            )}
          />
          <Controller
            name='isCopyPasteElementsCaptured'
            control={control}
            render={({ field }) => (
              <Toggle size='xs' label='Record copy-paste field names' {...field} />
            )}
          />

          <Controller
            name='isClickElementsCaptured'
            control={control}
            render={({ field }) => (
              <Toggle size='xs' label='Collect Click Element Names' {...field} />
            )}
          />

          <div className='flex gap-5'>
            <Button type='submit'>Save</Button>
            <Button variant='secondary' onClick={() => onNavigateToTrainingDataStudio()}>
              Go To Training Data Studio
            </Button>
            <Button
              variant='white'
              iconStart={<ArrowDownTrayIcon />}
              onClick={() => setIsTrainingDataCsvDownloadModalOpen(true)}
            >
              Download Training Data csv
            </Button>
          </div>
        </form>
      </ContentDisplay>

      <TrainingDataCsvDownloadModal
        isOpen={isTrainingDataCsvDownloadModalOpen}
        appName={appName}
        onClose={() => setIsTrainingDataCsvDownloadModalOpen(false)}
      />
    </>
  )
}

export default DataCollectionGranularity

